/**
 * Pure reconciliation of live stream events into the reviewer's local comment
 * list — the logic the DocumentViewer / review-rail applies so a new comment,
 * reply or status change appears WITHOUT a reload.
 *
 * The functions are deterministic and side-effect-free (easy to unit-test and
 * safe to call inside a React state updater). Upsert is DEDUPE-SAFE by comment
 * id: a live event that races the initial fetch — or a reconnect replay that
 * re-delivers an event — updates in place instead of double-inserting. The
 * caller re-anchors the resulting list against the DOM exactly as it does today.
 */

import type { Annotation, ReactionGroup } from "../api/types";
import type { LiveComment, ReactionEnvelope, StreamEvent } from "./events";

/**
 * Insert `comment` if new, else replace the existing entry with the same id
 * (in place — preserves list order so anchors don't jump). Dedupe-safe.
 */
export function upsertComment(
  list: Annotation[],
  comment: LiveComment,
): Annotation[] {
  const idx = list.findIndex((c) => c.id === comment.id);
  if (idx === -1) return [...list, comment];
  const next = list.slice();
  next[idx] = comment;
  return next;
}

/** Set the review status of comment `id`, if present (no-op otherwise). The
 *  wire status is an untyped string; it is one of the valid `CommentStatus`
 *  values (backend-validated) so we narrow it at this single boundary. */
export function patchStatus(
  list: Annotation[],
  id: number,
  status: string,
): Annotation[] {
  const idx = list.findIndex((c) => c.id === id);
  if (idx === -1) return list;
  const next = list.slice();
  next[idx] = { ...next[idx], status: status as Annotation["status"] };
  return next;
}

/**
 * review-notes §4.5: apply a live `comment-reaction` event to comment `id`'s
 * `reactions` — set the group's `count` for `emoji`, keep `who` in sync with the
 * actor, and drop the group when it empties. The RECEIVER's own `reacted_by_me`
 * is left untouched (their toggle is authoritative locally — this event carries
 * the ACTOR's flag, not the receiver's). A reaction on a comment the receiver
 * cannot see never arrives here (the backend fans out a body-less refresh for
 * restricted comments), so no visibility leak can enter the list.
 */
export function patchReaction(
  list: Annotation[],
  data: ReactionEnvelope,
): Annotation[] {
  const idx = list.findIndex((c) => c.id === data.comment_id);
  if (idx === -1) return list;
  const target = list[idx];
  const groups: ReactionGroup[] = (target.reactions ?? []).map((g) => ({
    ...g,
    who: [...g.who],
  }));
  let group = groups.find((g) => g.emoji === data.emoji);
  if (data.reacted_by_actor) {
    if (!group) {
      group = { emoji: data.emoji, count: 0, reacted_by_me: false, who: [] };
      groups.push(group);
    }
    if (!group.who.some((u) => u.user_id === data.actor_id))
      group.who.push({ user_id: data.actor_id, name: data.actor_name });
    group.count = data.count;
  } else if (group) {
    group.who = group.who.filter((u) => u.user_id !== data.actor_id);
    group.count = data.count;
  }
  const next = list.slice();
  next[idx] = {
    ...target,
    reactions: groups.filter((g) => g.count > 0),
  };
  return next;
}

/**
 * review-notes §4.4: the VIEWER's OWN optimistic reaction toggle (the local patch
 * applied the instant they tap a chip / pick an emoji, before the server round-trip
 * settles). Flips `reacted_by_me` for `emoji` on comment `id` and adds/removes the
 * viewer from the who-list + count. Distinct from `patchReaction` (a REMOTE actor's
 * event, which must NOT touch the viewer's own flag). Non-mutating.
 */
export function toggleOwnReaction<T extends Annotation>(
  list: T[],
  id: number,
  emoji: string,
  add: boolean,
  viewer: { id: number; name: string },
): T[] {
  const idx = list.findIndex((c) => c.id === id);
  if (idx === -1) return list;
  const target = list[idx];
  const groups: ReactionGroup[] = (target.reactions ?? []).map((g) => ({
    ...g,
    who: [...g.who],
  }));
  let group = groups.find((g) => g.emoji === emoji);
  if (add) {
    if (!group) {
      group = { emoji, count: 0, reacted_by_me: false, who: [] };
      groups.push(group);
    }
    if (!group.who.some((u) => u.user_id === viewer.id))
      group.who.push({ user_id: viewer.id, name: viewer.name });
    group.reacted_by_me = true;
    group.count = group.who.length;
  } else if (group) {
    group.who = group.who.filter((u) => u.user_id !== viewer.id);
    group.reacted_by_me = false;
    group.count = group.who.length;
  }
  const next = list.slice();
  next[idx] = { ...target, reactions: groups.filter((g) => g.count > 0) };
  return next;
}

/**
 * review-notes §4.2: reconcile the server ECHO of a reaction toggle into the list —
 * replace comment `id`'s group for `echo.emoji` with the authoritative server group
 * (count + who + reacted_by_me), dropping it when the count is 0. Non-mutating.
 */
export function applyReactionEcho<T extends Annotation>(
  list: T[],
  id: number,
  echo: ReactionGroup,
): T[] {
  const idx = list.findIndex((c) => c.id === id);
  if (idx === -1) return list;
  const target = list[idx];
  const others = (target.reactions ?? []).filter((g) => g.emoji !== echo.emoji);
  const next = list.slice();
  next[idx] = {
    ...target,
    reactions: echo.count > 0 ? [...others, echo] : others,
  };
  return next;
}

/**
 * Fold ONE stream event into the comment list for the currently open document.
 * Events for other documents (or the workspace-wide `activity` feed) leave the
 * list untouched — the doc rail only reflects its own document. Returns the
 * SAME array reference when nothing changed, so a React consumer can skip the
 * re-anchor when there is no visible delta.
 */
export function reduceCommentsForDoc(
  list: Annotation[],
  docId: number,
  event: StreamEvent,
): Annotation[] {
  switch (event.name) {
    case "comment-created":
    case "reply":
      if (event.data.doc_id !== docId) return list;
      return upsertComment(list, event.data.comment);
    case "status-change":
      if (event.data.doc_id !== docId) return list;
      return patchStatus(list, event.data.id, event.data.status);
    case "comment-reaction":
      if (event.data.doc_id !== docId) return list;
      return patchReaction(list, event.data);
    case "activity":
      return list;
  }
}

/** One rendered activity-feed row (the workspace-wide live activity view). */
export interface ActivityRow {
  key: string;
  doc_id: number;
  actor: string;
  /** Human summary, e.g. "commented", "replied", "marked as resolved". */
  verb: string;
  /** Client-stamped arrival time (a live event is "just now"). */
  at: number;
}

let activitySeq = 0;

/**
 * Map a stream event to an activity-feed row. Comment/reply/status events all
 * become activity (so the feed updates live even without a dedicated `activity`
 * frame); an explicit `activity` frame maps directly. Returns null for events
 * that carry no actor (none today — kept total for forward-compat).
 */
export function activityRowFromEvent(event: StreamEvent): ActivityRow | null {
  activitySeq += 1;
  const key = `a${activitySeq}`;
  const at = Date.now();
  switch (event.name) {
    case "comment-created":
      return {
        key,
        doc_id: event.data.doc_id,
        actor: event.data.comment.author_name,
        verb: "commented",
        at,
      };
    case "reply":
      return {
        key,
        doc_id: event.data.doc_id,
        actor: event.data.comment.author_name,
        verb: "replied",
        at,
      };
    case "status-change":
      return {
        key,
        doc_id: event.data.doc_id,
        actor: event.data.by,
        verb: `marked as ${event.data.status}`,
        at,
      };
    case "comment-reaction":
      return {
        key,
        doc_id: event.data.doc_id,
        actor: event.data.actor_name,
        verb: event.data.reacted_by_actor
          ? `reacted ${event.data.emoji}`
          : `removed a ${event.data.emoji} reaction`,
        at,
      };
    case "activity":
      return {
        key,
        doc_id: event.data.doc_id,
        actor: event.data.actor,
        verb: event.data.status
          ? `marked as ${event.data.status}`
          : verbForKind(event.data.kind),
        at,
      };
  }
}

function verbForKind(kind: string): string {
  switch (kind) {
    case "comment":
      return "commented";
    case "reply":
      return "replied";
    case "status":
      return "changed status";
    default:
      return kind;
  }
}
